import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useHistory } from 'react-router';
import { Link, useRouteMatch } from 'react-router-dom';
import { useDeepCompareEffect, useScroll, useUnmount } from 'react-use';

import { resolvePathname } from '@mssgme/helpers';
import { productsApi } from '@mssgme/lib';
import { Spinner } from '@mssgme/ui';
import { CardMoreButton } from '../../CardMoreButton';
import { CardProductsList, ProductItem } from '../../Products';
import styles from './CardProductsPage.scss';

function CardProductsPage({ card, setLastCollectionSlug, firstLoad, setFirstLoad, collectionSlug }) {
    const match = useRouteMatch();
    const history = useHistory();
    const { workspaceId, collections } = card;
    const currentCollection = collections.items.find((col) => col.slug === collectionSlug);
    const publishedProductsCount = (currentCollection && currentCollection.publishedProductsCount) || 0;
    const [products, setProducts] = useState({ hasNext: false, next: null, publishedCount: 0, results: [] });
    const { hasNext, next, results } = products;
    const [isFetching, setIsFetching] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const backPath = resolvePathname(match.url, '../../');
    let unmountTimeout;
    const scrollRef = React.useRef(null);
    const { y } = useScroll(scrollRef);

    const getProducts = async (next, collection, published, resetStore) => {
        setIsFetching(true);
        const productsStore = await productsApi.list(workspaceId, { next, collection, published });
        setProducts((prevProducts) => ({
            ...productsStore,
            results: resetStore ? productsStore.results : prevProducts.results.concat(productsStore.results),
        }));
        setIsFetching(false);
    };

    const unmount = (e, backPath) => {
        e.preventDefault();

        setLastCollectionSlug(false);
        setIsLoaded(false);

        unmountTimeout = setTimeout(() => {
            history.push(backPath);
        }, 610);
    };

    useDeepCompareEffect(() => {
        const initProducts = async () => {
            await getProducts(null, currentCollection._id, true, true);
            setIsLoaded(true);
            setFirstLoad(false);
        };

        initProducts();
    }, [publishedProductsCount, collections]);

    useEffect(() => {
        setLastCollectionSlug(currentCollection.slug);
    }, []);

    useUnmount(() => {
        clearTimeout(unmountTimeout);
    });

    return (
        <div className={cn({ [styles.root]: true, [styles.loaded]: isLoaded, [styles.noAnimation]: firstLoad })}>
            <div className={cn({ [styles.header]: true, [styles.shadow]: y > 0 })}>
                <Link onClick={(e) => unmount(e, backPath)} to={backPath} className={styles.backButton}>
                    <FiChevronLeft size={22} color="#000" />
                </Link>
                <h1 className={styles.heading}>{currentCollection && currentCollection.name}</h1>
            </div>
            <div className={styles.content} ref={scrollRef}>
                {results.length > 0 && (
                    <CardProductsList itemClassName={styles.productItem}>
                        {results.map((item) => (
                            <ProductItem
                                key={item._id}
                                tag={Link}
                                product={item}
                                to={{
                                    pathname: resolvePathname(match.url, '../../p/', item.slug),
                                    state: {
                                        fromCollection: currentCollection._id,
                                    },
                                }}
                            />
                        ))}
                    </CardProductsList>
                )}
                {isFetching && <Spinner />}
                {hasNext && !isFetching && (
                    <div className={styles.moreBtnContainer}>
                        <CardMoreButton
                            className={styles.moreBtn}
                            onClick={() => getProducts(next, currentCollection._id, true)}
                            disabled={isFetching}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

CardProductsPage.propTypes = {
    card: PropTypes.object.isRequired,
    isAdmin: PropTypes.bool,
    setLastCollectionSlug: PropTypes.func.isRequired,
    firstLoad: PropTypes.bool.isRequired,
    setFirstLoad: PropTypes.func.isRequired,
    collectionSlug: PropTypes.string.isRequired,
};

export default CardProductsPage;
